import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { ControlledSelect, ControlledSelectProps } from "./controlled";

interface SearchableControlledSelectProps extends Omit<
  ControlledSelectProps,
  "options" | "onSearchChange" | "loading"
> {
  useRequestQuery: (options: {
    page: number;
    pageSize: number;
    searchParams: URLSearchParams;
  }) => {
    data?: { data?: any[] };
    isLoading: boolean;
  };
  convertItemToOption: (item: any) => { value: string; label: string };
  extraOptions?: { value: string; label: string }[];
}

/**
 * {@link ControlledSelect} backed by a server-side search: it debounces the
 * query, refetches through `useRequestQuery`, and caches the selected option so
 * its label survives even when it falls out of the current results. Filtering is
 * done by the server (the Select's client-side filtering is switched off).
 */
export function SearchableControlledSelect({
  name,
  useRequestQuery,
  convertItemToOption,
  extraOptions = [],
  ...rest
}: SearchableControlledSelectProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [optionsCache, setOptionsCache] =
    useState<{ value: string; label: string }[]>(extraOptions);

  const { control } = useFormContext();
  const currentValue = useWatch({ control, name });

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();
      if (trimmed.length >= 3 || trimmed.length === 0) {
        setDebouncedSearchTerm(trimmed);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: queryResponse, isLoading } = useRequestQuery({
    page: 1,
    pageSize: 100,
    searchParams: new URLSearchParams({ s: debouncedSearchTerm }),
  });

  // Keep the selected option's label available even when it's not in the
  // current search results (so the field doesn't fall back to the raw code).
  useEffect(() => {
    const nextCache = [...extraOptions];
    if (currentValue) {
      const cached = optionsCache.find((o) => o.value === currentValue);
      const inResults = (queryResponse?.data || [])
        .map(convertItemToOption)
        .find((o) => o.value === currentValue);
      if (!extraOptions.some((o) => o.value === currentValue)) {
        if (inResults) nextCache.push(inResults);
        else if (cached) nextCache.push(cached);
        else nextCache.push({ value: currentValue, label: `${currentValue}` });
      }
    }
    setOptionsCache(nextCache);
  }, [currentValue, queryResponse?.data, convertItemToOption, extraOptions]);

  const options = useMemo(
    () => (queryResponse?.data || []).map(convertItemToOption),
    [queryResponse?.data, convertItemToOption],
  );

  const deduplicatedOptions = useMemo(() => {
    const all = [...optionsCache, ...options];
    const seen = new Set<string>();
    return all.filter((option) => {
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
  }, [optionsCache, options]);

  return (
    <ControlledSelect
      {...rest}
      name={name}
      options={deduplicatedOptions}
      loading={isLoading}
      onSearchChange={setSearchTerm}
    />
  );
}

export default SearchableControlledSelect;
